import React, { useEffect } from 'react';

import { Controller, useForm } from 'react-hook-form';

import { Alert, GridLegacy as Grid, InputAdornment, MenuItem, TextField } from '@mui/material';

import getAllCurrencyCodes from '~/utils/getAllCurrencyCodes';

import { StayInputOverrides } from './otaParity/types';

export type StayInputsFormValues = {
  nights: number;
  baseSellPriceAud: number;
  currencyCode: string;
  fxRateAudToNative: number;
  prvInclusionsAud: number;
  prvInclusionsNative: number | null;
};

const DEFAULT_VALUES: StayInputsFormValues = {
  nights: 3,
  baseSellPriceAud: 0,
  currencyCode: 'AUD',
  fxRateAudToNative: 1,
  prvInclusionsAud: 0,
  prvInclusionsNative: null,
};

function toFormValues(overrides: StayInputOverrides): StayInputsFormValues {
  return {
    nights: overrides.nights,
    baseSellPriceAud: overrides.baseSellPriceAud,
    currencyCode: overrides.currencyCode,
    fxRateAudToNative: overrides.fxRateAudToNative,
    prvInclusionsAud: overrides.prvInclusionsAud,
    prvInclusionsNative: overrides.prvInclusionsNative,
  };
}

interface Props {
  /**
   * The session's stay inputs, so returning to this tab restores what the CM left rather than
   * resetting to AUD. A reset currency clears every typed surcharge cost, which is silent.
   */
  initialValues?: StayInputOverrides | null;
  onChange: (values: StayInputOverrides) => void;
}

export default function StayInputsForm({ initialValues, onChange }: Props) {
  const { control, watch } = useForm<StayInputsFormValues>({
    defaultValues: initialValues ? toFormValues(initialValues) : DEFAULT_VALUES,
    mode: 'onChange',
  });
  const values = watch();

  useEffect(() => {
    onChange({
      nights: values.nights,
      currencyCode: values.currencyCode,
      fxRateAudToNative: values.currencyCode === 'AUD' ? 1 : values.fxRateAudToNative,
      baseSellPriceAud: values.baseSellPriceAud,
      prvInclusionsAud: values.prvInclusionsAud,
      prvInclusionsNative: values.currencyCode === 'AUD' ? null : values.prvInclusionsNative,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    values.nights,
    values.baseSellPriceAud,
    values.currencyCode,
    values.fxRateAudToNative,
    values.prvInclusionsAud,
    values.prvInclusionsNative,
  ]);

  const isNonAud = values.currencyCode !== 'AUD';

  return (
    <Grid container spacing={2} alignItems="flex-start">
      <Grid item xs={6} sm={3} md={2}>
        <Controller
          name="nights"
          control={control}
          rules={{ min: { value: 1, message: '1-21 nights' }, max: { value: 21, message: '1-21 nights' } }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              onChange={(e) => field.onChange(+e.target.value)}
              label="Nights"
              type="number"
              size="small"
              fullWidth
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              InputProps={{ inputProps: { min: 1, max: 21 } }}
            />
          )}
        />
      </Grid>
      <Grid item xs={6} sm={3} md={2}>
        <Controller
          name="baseSellPriceAud"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              onChange={(e) => field.onChange(+e.target.value)}
              label="Base sell price"
              type="number"
              size="small"
              fullWidth
              InputProps={{ startAdornment: <InputAdornment position="start">AUD</InputAdornment> }}
            />
          )}
        />
      </Grid>
      <Grid item xs={6} sm={3} md={2}>
        <Controller
          name="currencyCode"
          control={control}
          render={({ field }) => (
            <TextField {...field} label="Currency" select size="small" fullWidth>
              {getAllCurrencyCodes().map((code) => (
                <MenuItem key={code} value={code}>
                  {code}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
      </Grid>
      {isNonAud && (
        <Grid item xs={6} sm={3} md={2}>
          <Controller
            name="fxRateAudToNative"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                onChange={(e) => field.onChange(+e.target.value)}
                label={`FX rate (AUD → ${values.currencyCode})`}
                type="number"
                size="small"
                fullWidth
              />
            )}
          />
        </Grid>
      )}
      <Grid item xs={6} sm={3} md={2}>
        <Controller
          name="prvInclusionsAud"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              onChange={(e) => field.onChange(+e.target.value)}
              label="Inclusion PRV value"
              type="number"
              size="small"
              fullWidth
              InputProps={{ startAdornment: <InputAdornment position="start">AUD</InputAdornment> }}
            />
          )}
        />
      </Grid>
      {isNonAud && (
        <Grid item xs={6} sm={3} md={2}>
          <Controller
            name="prvInclusionsNative"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                value={field.value ?? ''}
                onChange={(e) => field.onChange(e.target.value === '' ? null : +e.target.value)}
                label="Inclusion PRV value (native)"
                type="number"
                size="small"
                fullWidth
                InputProps={{ startAdornment: <InputAdornment position="start">{values.currencyCode}</InputAdornment> }}
              />
            )}
          />
        </Grid>
      )}
      <Grid item xs={12}>
        <Alert severity="info" variant="outlined" sx={{ mt: 1 }}>
          Manual inputs until Property Setup, Package Options, Surcharges, and Blackouts are wired in from Salesforce.
        </Alert>
      </Grid>
    </Grid>
  );
}
